import {
  SimulationJobRun,
  useSimulationJobHistoryListCreateQuery,
  useSimulationSimulationModelVersionListQuery,
} from "@app/api/carApi.generated";
import { CarSelectOption, CarSelectOptionGroup } from "@app/components/Inputs";
import { refetchOnFocus } from "@app/const";
import { useMemo } from "react";
import { checkDefined, formatDateTime } from "@app/utils";
import { seedTypeOptions } from "./SimulationSettingsDialog";
import { Box } from "@mui/material";

export interface CarSimulationJobOption extends CarSelectOption<string> {
  groupId?: string;
}

export const useSimulationList = (params: { skip?: boolean }) => {
  const { data, isLoading } = useSimulationJobHistoryListCreateQuery(
    undefined,
    {
      refetchOnFocus,
      skip: params.skip,
    },
  );

  const modelVersions = useSimulationSimulationModelVersionListQuery({
    id: "", // todo fix in swagger
  });

  return useMemo(() => {
    const typedData: Record<string, SimulationJobRun[]> | undefined =
      data as any;

    const getName = (item: SimulationJobRun) => {
      if (item.created_by.first_name || item.created_by.last_name) {
        return `${item.created_by.first_name} ${item.created_by.last_name}`;
      } else {
        return item.created_by.email;
      }
    };

    const sortRuns = (a: SimulationJobRun, b: SimulationJobRun) => {
      const sortA = a.published ? 0 : 1;
      const sortB = b.published ? 0 : 1;
      return (
        sortA - sortB || (b.created_at ?? "").localeCompare(a.created_at ?? "")
      );
    };

    const simJobRunToOption = (
      i: SimulationJobRun,
    ): CarSimulationJobOption => ({
      value: checkDefined(i.id),
      label: `${formatDateTime(checkDefined(i.created_at))} ${getName(i)}`,
      hint: (
        <Box
          sx={{
            display: "grid",
            columnGap: 4,
            rowGap: 1,
            gridTemplateColumns: "max-content max-content",
          }}
        >
          <div>Use boundaries:</div>
          <div>{i.settings_hist.use_boundaries ? "Yes" : "No"}</div>
          <div>Use crisis mode:</div>
          <div>{i.settings_hist.use_crisis_mode ? "Yes" : "No"}</div>
          <div>Distributions:</div>
          <div>
            {i.settings_hist.use_empirical_distribution
              ? "Empirical"
              : "Normal"}
          </div>
          <div>Model version:</div>
          <div>
            {
              modelVersions.data?.find(
                (v) => v.id === i.settings_hist.model_version,
              )?.full_version
            }
          </div>
          <div>Seed type:</div>
          <div>
            {
              seedTypeOptions.find(
                (o) => o.value === i.settings_hist.seed_settings,
              )?.label
            }
          </div>
        </Box>
      ),
      isBold: i.published,
      groupId: i.group.id,
    });

    const staticOptions = Array.from(typedData?.["STATIC"] ?? [])
      .sort(sortRuns)
      .map(simJobRunToOption);

    const dynamicOptions = Array.from(typedData?.["DYNAMIC"] ?? [])
      .sort(sortRuns)
      .map<CarSimulationJobOption>(simJobRunToOption);

    const groups: CarSelectOptionGroup<string>[] = [
      {
        label: "Price - Driven",
        items: dynamicOptions,
      },
      {
        label: "Static",
        items: staticOptions,
      },
    ];

    return {
      staticOptions,
      dynamicOptions,
      groups,
      isSimulationStatic: (id: string) =>
        staticOptions.some((i) => i.value === id),
      getSimulationName: (id: string) =>
        staticOptions.find((i) => i.value === id)?.label ??
        dynamicOptions.find((i) => i.value === id)?.label,
      isLoading: isLoading || modelVersions.isLoading,
    };
  }, [data, isLoading, modelVersions.data, modelVersions.isLoading]);
};

export type UseSimulationList = ReturnType<typeof useSimulationList>;
